import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import AdminLayout from './admin/components/AdminLayout';
import Login from './admin/pages/Login';
import Users from './admin/pages/Users';
import Forms from './admin/pages/Forms';
import FormDetail from './admin/pages/FormDetail';
import DistrictManagement from './admin/pages/DistrictManagement';
import MasterDataManagement from './admin/pages/MasterDataManagement';
import Analytics from './admin/pages/Analytics';
import Chatbot from './admin/pages/Chatbot';
import KnowledgeBase from './admin/pages/KnowledgeBase';
import SupervisorDashboard from './admin/pages/SupervisorDashboard';
import SupervisorReports from './admin/pages/SupervisorReports';
import WorkerVisitDetail from './admin/pages/WorkerVisitDetail';
import Approvals from './admin/pages/Approvals';
import PrivacyPolicy from './pages/PrivacyPolicy';

const theme = createTheme({
  typography: {
    fontSize: 15,
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  palette: {
    primary: {
      main: '#2563EB',
      light: '#3B82F6',
      dark: '#1D4ED8',
      lighter: '#EFF6FF',
    },
    secondary: {
      main: '#7C3AED',
      light: '#8B5CF6',
      dark: '#6D28D9',
      lighter: '#F5F3FF',
    },
    success: {
      main: '#10B981',
      light: '#34D399',
      dark: '#059669',
      lighter: '#ECFDF5',
    },
    warning: {
      main: '#F59E0B',
      light: '#FBBF24',
      dark: '#D97706',
      lighter: '#FFFBEB',
    },
    error: {
      main: '#EF4444',
      light: '#F87171',
      dark: '#DC2626',
      lighter: '#FEF2F2',
    },
    info: {
      main: '#06B6D4',
      light: '#22D3EE',
      dark: '#0891B2',
      lighter: '#ECFEFF',
    },
    grey: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827',
    },
    background: {
      default: '#F9FAFB',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#111827',
      secondary: '#6B7280',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
      `,
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          border: '1px solid #E5E7EB',
          borderRadius: 8,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
  },
});

function ProtectedRoute({ children }) {
  const token = localStorage.getItem('admin_token');
  if (!token) {
    return <Navigate to="/admin/login" replace />;
  }
  return children;
}

// A SUPERVISOR's default/unmatched landing spot is their own dashboard, not
// Analytics — Analytics pulls in system-wide activity logs that aren't PHC
// scoped, so a supervisor must never even briefly mount it, not just be
// redirected away from it after the fact.
function DefaultAdminRedirect() {
  let role = null;
  try {
    role = JSON.parse(localStorage.getItem('admin_user') || 'null')?.role;
  } catch {
    role = null;
  }
  const target = role === 'SUPERVISOR'
    ? '/admin/supervisor/dashboard'
    : role === 'TALUKA_ADMIN'
    ? '/admin/taluka-admin/dashboard'
    : role === 'ANM'
    ? '/admin/anm/approvals'
    : '/admin/analytics';
  return <Navigate to={target} replace />;
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Navigate to="/admin/login" replace />} />
          <Route path="/admin/login" element={<Login />} />

          {/* Public Routes */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />

          {/* Protected Admin Routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DefaultAdminRedirect />} />
            <Route path="dashboard" element={<DefaultAdminRedirect />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="users" element={<Users />} />
            <Route path="forms" element={<Forms />} />
            <Route path="forms/hbyc" element={<Forms />} />
            <Route path="forms/hbnc" element={<Forms />} />
            <Route path="forms/nrc" element={<Forms />} />
            <Route path="forms/sncu" element={<Forms />} />
            <Route path="forms/:formType/:formId" element={<FormDetail />} />
            <Route path="districts" element={<DistrictManagement />} />
            <Route path="master-data" element={<MasterDataManagement />} />
            <Route path="reports/asha-visits" element={<SupervisorReports />} />
            <Route path="reports/asha-visits/:ashaWorkerId" element={<WorkerVisitDetail />} />
            <Route path="approvals" element={<Approvals />} />
            <Route path="chatbot" element={<Chatbot />} />
            <Route path="knowledge-base" element={<KnowledgeBase />} />
            {/* SUPERVISOR's own restricted pages — AdminLayout hides every
                other route above from them and bounces any direct URL
                access back to supervisor/dashboard. */}
            <Route path="supervisor/dashboard" element={<SupervisorDashboard />} />
            <Route path="supervisor/users" element={<Users />} />
            <Route path="supervisor/approvals" element={<Approvals />} />
            <Route path="supervisor/reports" element={<SupervisorReports />} />
            <Route path="supervisor/reports/:ashaWorkerId" element={<WorkerVisitDetail />} />
            <Route path="supervisor/forms/:formType/:formId" element={<FormDetail />} />
            {/* TALUKA_ADMIN's own restricted pages — same reused-component
                pattern as SUPERVISOR above, scoped by taluka instead of PHC
                on the backend (see get_scoped_asha_worker_ids). */}
            <Route path="taluka-admin/dashboard" element={<SupervisorDashboard />} />
            <Route path="taluka-admin/users" element={<Users />} />
            <Route path="taluka-admin/master-data" element={<MasterDataManagement />} />
            <Route path="taluka-admin/approvals" element={<Approvals />} />
            <Route path="taluka-admin/reports" element={<SupervisorReports />} />
            <Route path="taluka-admin/reports/:ashaWorkerId" element={<WorkerVisitDetail />} />
            <Route path="taluka-admin/forms/:formType/:formId" element={<FormDetail />} />

            {/* ANM — scoped to their assigned ASHA workers (anm_id), same
                pattern as SUPERVISOR/TALUKA_ADMIN above, via
                get_scoped_asha_worker_ids on the backend. Approvals only —
                no dashboard/users/reports page for this role yet. */}
            <Route path="anm/approvals" element={<Approvals />} />
            <Route path="anm/forms/:formType/:formId" element={<FormDetail />} />

            {/* Any other unmatched admin path fails safe instead of rendering
                blank — this is exactly the bug that hit FormDetail's "परत"
                button when it pointed at a path with no matching route. */}
            <Route path="*" element={<DefaultAdminRedirect />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
